<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import AccordionObjectivesList from '../objectives/AccordionObjectivesList.vue'
import { useAnchorPageStore } from '../anchor_page.store'
import type { AnchorObjective } from '../anchor_page_types'
import type { ObjectiveLevel } from '../objectives/objective_levels'
import { useAnchorPageTracking } from '../useAnchorPageTracking'
import { useObjectiveResourceMaps } from './useObjectiveResourceMaps'

withDefaults(defineProps<{
  isReadOnly?: boolean
  readOnlyHint?: string
  levelsWithGeneratedAssessments?: ObjectiveLevel[]
}>(), {
  isReadOnly: false,
  readOnlyHint: undefined,
  levelsWithGeneratedAssessments: () => []
})

defineSlots<{
  generatedAssessments?(props: { objective: AnchorObjective, index: number }): unknown
}>()

const anchorPageStore = useAnchorPageStore()
const { content, customObjectives, accessInfo, strategyMap, isFetchingCustomization } = storeToRefs(anchorPageStore)
const { editObjectiveText } = useAnchorPageTracking(getAnalyticsTracker())

const objectives = computed(() => content.value?.objectives ?? [])
const objectiveTexts = computed(() => customObjectives.value.map((objective) => objective.text))
const { resourcesByObjectId, isFetchingResourcesByObjectId, selectedLanguages } = useObjectiveResourceMaps(objectives)
</script>

<template>
  <AccordionObjectivesList
    v-model:selected-languages="selectedLanguages"
    :objectives="objectives"
    :objective-texts="objectiveTexts"
    :page-uuid="content?.uuid"
    :is-editable="!accessInfo.blur"
    :is-locked="accessInfo.blur"
    :is-fetching="isFetchingCustomization"
    :strategies="strategyMap"
    :resources-by-object-id="resourcesByObjectId"
    :is-fetching-resources-by-object-id="isFetchingResourcesByObjectId"
    :is-read-only="isReadOnly"
    :read-only-hint="readOnlyHint"
    :levels-with-generated-assessments="levelsWithGeneratedAssessments"
    @update:objective="editObjectiveText"
  >
    <template #generatedAssessments="slotProps">
      <slot name="generatedAssessments" v-bind="slotProps" />
    </template>
  </AccordionObjectivesList>
</template>
